import React, { useState, useEffect } from 'react'; // Hooks add kiye
import { Box, Typography, Grid, Paper, Container } from '@mui/material';
import { styled, keyframes } from '@mui/system';
// Firebase imports
import { db } from '../firebaseConfig'; 
import { collection, getDocs } from 'firebase/firestore';

// --- Animations (Same as yours) ---
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const twinkle = keyframes`
  0% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0; transform: scale(0.5); }
`;

// --- Styled Components (Same as yours) ---
const Star = styled(Box)(({ size, top, left, delay, duration }) => ({
    position: 'absolute',
    top: top,
    left: left,
    width: size,
    height: size,
    backgroundColor: '#fff',
    borderRadius: '50%',
    boxShadow: '0 0 10px #fff',
    animation: `${twinkle} ${duration}s infinite ease-in-out ${delay}s`,
    zIndex: 0,
}));

const ModernCard = styled(Paper)(({ bordercolor }) => ({
    padding: '25px 15px',
    width: '10vh',
    background: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(16px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    textAlign: 'center',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    animation: `${float} 4s ease-in-out infinite`,
    height: '100%', 
    zIndex: 1,
    position: 'relative',
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.07)',
        border: `1px solid ${bordercolor}`,
        boxShadow: `0 0 30px ${bordercolor}44`,
        transform: 'scale(1.05) translateY(-5px)',
        '& .skill-visual': {
            transform: 'scale(1.1) rotate(5deg)',
            filter: `drop-shadow(0 0 10px ${bordercolor})`,
        }
    }
}));

const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    size: Math.random() * 2 + 1 + 'px',
    top: Math.random() * 100 + '%',
    left: Math.random() * 100 + '%',
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 2,
}));

const SkillVisual = ({ icon, color }) => {
    const visualStyle = {
        width: 42,
        height: 42,
        objectFit: 'contain',
        transition: 'all 0.3s ease',
    };
    return <Box component="img" src={icon} sx={visualStyle} className="skill-visual" alt="skill" />;
};

const SkillsSection = () => {
    // 1. Local State for Firestore Data
    const [skillCategories, setSkillCategories] = useState([]);

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "skills"));
                const allSkills = querySnapshot.docs.map(doc => doc.data());

                // 2. Logic to group skills by category (UI compatible)
                const grouped = allSkills.reduce((acc, skill) => {
                    const catName = skill.category || "General";
                    const existingCat = acc.find(c => c.category === catName);
                    
                    if (existingCat) {
                        existingCat.skills.push(skill);
                    } else {
                        acc.push({ category: catName, skills: [skill] });
                    }
                    return acc;
                }, []);

                setSkillCategories(grouped);
            } catch (error) {
                console.error("Firestore Fetch Error:", error);
            }
        };

        fetchSkills();
    }, []);

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', background: '#030712', py: 12, position: 'relative', overflow: 'hidden' }}>
            
            {stars.map((star) => (
                <Star key={star.id} size={star.size} top={star.top} left={star.left} delay={star.delay} duration={star.duration} />
            ))}

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <Box sx={{ mb: 10, textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ color: '#3b82f6', fontWeight: 800, letterSpacing: '3px', fontSize: '0.9rem' }}>
                        TECH STACK
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', mt: 1, letterSpacing: '-1px', fontSize: {xs: '2rem', md: '3.5rem'} }}>
                        Innovating Through <br />
                        <span style={{ color: '#3b82f6' }}>Modern Technologies</span>
                    </Typography>
                </Box>

                {/* Categories Map (Dynamic Data) */}
                {skillCategories.map((cat, idx) => (
                    <Box key={idx} sx={{ mb: 8 }}>
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                            {cat.category}
                            <Box sx={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
                        </Typography>

                        <Grid container spacing={3}>
                            {cat.skills.map((skill, sIdx) => (
                                <Grid item xs={6} sm={4} md={3} lg={2.4} key={sIdx}>
                                    <ModernCard bordercolor={skill.color} elevation={0}>
                                        <Box sx={{ mb: 2 }}>
                                            <SkillVisual icon={skill.icon} color={skill.color} />
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '1px', fontSize: '0.75rem' }}>
                                            {skill.name}
                                        </Typography>
                                        <Box sx={{ width: '5px', height: '5px', borderRadius: '50%', bgcolor: skill.color, mt: 2, boxShadow: `0 0 10px ${skill.color}` }} />
                                    </ModernCard>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Container>
        </Box>
    );
};

export default SkillsSection;